import { useState } from 'react';

import { Button, Card, Icon, TextLink } from '@upside/flux-ui';

import { faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { faShieldHalved } from '@fortawesome/pro-solid-svg-icons';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

import { useSetTimeoutInMinutes } from './hooks/useSetTimeoutInMinutes';
import { TotpDetails, TotpSetupDetails } from './types';

export function TotpSetup({
  handleCancel,
  handleTimeout,
  totpSetupState,
  setTotpState,
}: {
  handleCancel: () => void;
  handleTimeout: () => void;
  totpSetupState: TotpSetupDetails;
  setTotpState: (totpDetails: TotpDetails) => void;
}) {
  const { t } = useTranslation('totpSetup');
  const [copied, setCopied] = useState(false);
  useSetTimeoutInMinutes(handleTimeout);

  const { sessionId, secretCode, email } = totpSetupState;
  const formattedCode = secretCode.match(/.{1,4}/g)?.join('-') || secretCode;

  const handleCopyCode = async () => {
    await navigator.clipboard.writeText(secretCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="mt-8 flex flex-col items-center gap-8"
      data-testid="totp-setup"
    >
      <Card className="relative flex w-full max-w-[410px] flex-col items-center gap-6 rounded-xl p-6 shadow-md">
        <div className="flex w-full flex-col items-center gap-3">
          <div className="flex size-12 items-center justify-center rounded-full border border-border-secondary bg-card-secondary">
            <Icon
              className="text-active-foreground-hover"
              icon={faShieldHalved}
              size="lg"
            />
          </div>
          <div className="flex flex-col items-center gap-2">
            <h1 className="text-center text-2xl font-medium text-foreground">
              {t('title')}
            </h1>
            <p className="text-center text-base text-foreground">
              {t('description')}
            </p>
          </div>
        </div>

        <div className="rounded border border-border p-2 text-active-foreground">
          <QRCodeSVG
            value={`otpauth://totp/Krakenflex:${email}?secret=${secretCode}&issuer=Krakenflex`}
            size={140}
            level="M"
            fgColor="currentColor"
            bgColor="transparent"
          />
        </div>

        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <p className="text-center text-base text-foreground">
              {t('copyCodeMessage')}
            </p>
            <div className="h-px flex-1 bg-border" />
          </div>

          <p className="pl-2 pr-2 text-center text-sm text-foreground-secondary">
            {formattedCode}
          </p>

          <Button
            variant="outline"
            colour="neutral"
            size="lg"
            onClick={handleCopyCode}
          >
            <Button.Label>
              {copied ? t('copiedButtonCopied') : t('copiedButtonCopy')}
            </Button.Label>
            <Button.Icon icon={copied ? faCheck : faCopy} />
          </Button>
        </div>

        <Button
          className="w-full"
          size="lg"
          onClick={() => {
            setTotpState({ sessionId, isSetup: true });
          }}
        >
          <Button.Label>{t('continue')}</Button.Label>
        </Button>
      </Card>

      <TextLink onClick={handleCancel} type="button" className="z-0">
        <TextLink.Label>{t('cancel')}</TextLink.Label>
      </TextLink>
    </div>
  );
}
